/**
 * Quantified client results, and how much a line of evidence is worth as a lead.
 *
 * Pure, model-free. WHY THIS EXISTS (ITC Tax Brief run 4eda4aae, 2026-10-01):
 * the source page carried a results tile (Energy / OFS: $10M property tax
 * savings, Harris County, Texas; Renewables: $200M valuation reduction;
 * Data Centers: $5M tax savings, Texas; Cement: $2M a year property tax
 * savings, Florida) and not one of those figures reached a Concept. The tile
 * is written as stacked fragments, so the sentence splitter produced pieces
 * of two or three words and `extractClaims` dropped every one of them. The
 * angles were then ranked on the claims that DID survive: an FAQ reassurance
 * ("We do not contact assessors without your authorization.") and a
 * credentials line ("led by former county appraisers, CPAs, CMIs ...").
 *
 * Three things live here:
 *  - `findResultTiles` stitches a results tile back into one verbatim claim;
 *  - `classifyEvidenceLine` / `evidenceTier` say whether a line is a result,
 *    support (reassurance, credentials) or neither, so a ranker can prefer
 *    results for a paid lead-generation Concept;
 *  - `isResultClaim` is the test the lane gate uses to open `proof_in_public`.
 */

const SCALE = String.raw`(?:[KkMmBb]\b|million\b|billion\b|thousand\b)`;
const MONEY = String.raw`[$£€]\s?\d[\d,]*(?:\.\d+)?\s?${SCALE}?`;
const PERCENT = String.raw`\d[\d,]*(?:\.\d+)?\s?%`;
export const FIGURE_SOURCE = String.raw`(?:${MONEY}|${PERCENT})`;

const RESULT_WORD =
  /\b(?:savings?|saved|saving|reduc\w*|increas\w*|growth|revenue|recover\w*|refund\w*|returns?|lift|roi|faster|cut|lower\w*)\b/i;

/**
 * `<label>. <figure>. <what the figure is>.[ <place>.]` — the shape a results
 * tile takes once its markup is flattened to text. The optional place is
 * refused when it is really the label of the NEXT tile (it is followed by a
 * figure).
 */
const TILE = new RegExp(
  String.raw`(?<=^|[.!?]\s)([A-Z][^.!?$%]{1,48}?)\.\s+(${FIGURE_SOURCE})\.\s+([^.!?]{3,80}?)\.(?:\s+([^.!?]{2,40}?)\.(?!\s+(?:[$£€]|\d)))?`,
  'g',
);

/**
 * The second tile shape a page prints, percentage first, money second, the
 * client described by a label and an optional qualifier:
 * `International Data Center Developer. Multi-State Portfolio 40% reduction.
 * $6M annually in property tax savings.` Production run e2f0d912 (2026-10-01)
 * had both of itctax.com's data-center tiles in this shape and `findResultTiles`
 * saw neither, because the first slot of `TILE` is a label followed by a
 * figure, never a label followed by a qualifier and a percentage.
 */
const TILE_PERCENT_FIRST = new RegExp(
  String.raw`(?<=^|[.!?]\s)([A-Z][^.!?$%]{1,60}?)\.\s+(?:([A-Z][^.!?$%]{1,40}?)\s+)?(${PERCENT})\s+(?:reduction|decrease|savings)\.\s+(${MONEY})\s+((?:annually|per year|a year|each year|yearly)\s+)?(?:in\s+)?([^.!?]{3,60}?)\.`,
  'g',
);

const MAX_TILES = 10;

/**
 * Every results tile in the text, each as the VERBATIM span of the page
 * (whitespace collapsed) so a downstream "quote must appear in the source"
 * check still holds. A tile whose "what" has no outcome word is not a result
 * (a "Founded. 1998." style stat is not a client outcome and has no figure of
 * the right shape anyway). Tiles come back in page order.
 */
export function findResultTiles(text: string): string[] {
  const flat = text.replace(/\s+/g, ' ');
  const hits: { index: number; span: string }[] = [];
  for (const match of flat.matchAll(TILE)) {
    if (!RESULT_WORD.test(match[3] ?? '')) continue;
    hits.push({ index: match.index ?? 0, span: match[0].trim() });
  }
  for (const match of flat.matchAll(TILE_PERCENT_FIRST)) {
    if (!RESULT_WORD.test(match[6] ?? '')) continue;
    hits.push({ index: match.index ?? 0, span: match[0].trim() });
  }
  hits.sort((a, b) => a.index - b.index);
  const found: string[] = [];
  const seen = new Set<string>();
  for (const { span } of hits) {
    if (seen.has(span)) continue;
    seen.add(span);
    found.push(span);
    if (found.length >= MAX_TILES) break;
  }
  return found;
}

const FIRST_PERSON_RESULT = new RegExp(
  String.raw`\b(?:we|our clients?|clients?)\b[^.!?]{0,80}\b(?:saved|reduced|recovered|cut|delivered|achieved|secured)\b[^.!?]{0,80}${FIGURE_SOURCE}`,
  'i',
);

/**
 * The brand's own page reporting a result in the THIRD person: "ITC Tax has
 * documented individual engagement results including a $6 million annual tax
 * reduction representing a 40% decrease in assessed value". It names no "we"
 * or "client", so `FIRST_PERSON_RESULT` missed it, and it was the strongest
 * proof on the data-centers page. A figure and an outcome word are not enough
 * (the same page says assessed values exceed market value "by 30-50%"), so the
 * sentence must also say the work PRODUCED it.
 */
const THIRD_PERSON_RESULT_CUE =
  /\b(?:documented|achieved|delivered|secured|produc(?:ed|ing)|resulted in|engagements?)\b/i;

function isThirdPersonResultSentence(text: string): boolean {
  return (
    THIRD_PERSON_RESULT_CUE.test(text) &&
    RESULT_WORD.test(text) &&
    new RegExp(FIGURE_SOURCE).test(text)
  );
}

/** A claim that states what the brand's own work produced, with a figure. */
export function isResultClaim(claim: string): boolean {
  const text = claim.replace(/\s+/g, ' ').trim();
  if (!text) return false;
  return (
    findResultTiles(text).length > 0 ||
    FIRST_PERSON_RESULT.test(text) ||
    isThirdPersonResultSentence(text)
  );
}

export type EvidenceKind = 'result' | 'credential' | 'reassurance' | 'other';

const CREDENTIAL =
  /\b(?:led by|former(?:ly)?\s+(?:county|state|city|irs|assessor|appraiser|auditor|big\s?4)|(?:CPAs?|CMIs?|CFAs?|CFEs?|MAI|attorneys?|licensed|certified|credentialed|accredited)\b|years of experience|decades of experience|award[- ]winning)/i;

const REASSURANCE =
  /\b(?:we\s+do\s+not|we\s+don['’]?t|we\s+never|do\s+not\s+contact|only\s+with\s+your|not\s+here\s+to|confidential(?:ly)?|commitment[- ]free|no\s+obligation|non[- ]disruptive|without\s+disrupting|NDA|white[- ]label|quiet(?:ly)?\s+collaboration|you\s+decide|you\s+gain\s+documented|deleted\s+after|retained\s+\d|least[- ]privilege)\b/i;

export function classifyEvidenceLine(line: string): EvidenceKind {
  if (isResultClaim(line)) return 'result';
  if (CREDENTIAL.test(line)) return 'credential';
  if (REASSURANCE.test(line)) return 'reassurance';
  return 'other';
}

export type EvidenceTier = 'result' | 'other' | 'support';

/**
 * What an angle's cited evidence is worth as a lead: `result` when ANY cited
 * line is a quantified result, `support` when every cited line is a
 * reassurance or a credential, `other` otherwise.
 */
export function evidenceTier(lines: readonly string[]): EvidenceTier {
  const kinds = lines.map(classifyEvidenceLine);
  if (kinds.includes('result')) return 'result';
  if (kinds.length > 0 && kinds.every((kind) => kind === 'reassurance' || kind === 'credential')) {
    return 'support';
  }
  return 'other';
}

const TIER_ORDER: Record<EvidenceTier, number> = { result: 2, other: 1, support: 0 };

/** Positive when `a` leads better than `b`. */
export function compareTiers(a: EvidenceTier, b: EvidenceTier): number {
  return TIER_ORDER[a] - TIER_ORDER[b];
}

/** Cited lines ordered result first, then the rest, then support. */
export function orderEvidenceByWorth(lines: readonly string[]): string[] {
  const rank = (line: string): number => {
    const kind = classifyEvidenceLine(line);
    return kind === 'result' ? 0 : kind === 'other' ? 1 : 2;
  };
  return lines
    .map((line, index) => ({ line, index, rank: rank(line) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.line);
}

/** One canonical value for a figure, so "$5M" and "$5 million" compare equal. */
export function canonicalFigure(raw: string): string | null {
  const text = raw.trim().toLowerCase();
  const percent = /^(\d[\d,]*(?:\.\d+)?)\s?%$/.exec(text);
  if (percent) return `${Number(percent[1]!.replace(/,/g, ''))}%`;
  const money = /^([$£€])\s?(\d[\d,]*(?:\.\d+)?)\s?(k|m|b|thousand|million|billion)?$/.exec(text);
  if (!money) return null;
  const scale =
    money[3] === 'k' || money[3] === 'thousand'
      ? 1e3
      : money[3] === 'm' || money[3] === 'million'
        ? 1e6
        : money[3] === 'b' || money[3] === 'billion'
          ? 1e9
          : 1;
  return `${money[1]}${Number(money[2]!.replace(/,/g, '')) * scale}`;
}

/** Money and percentage figures in a text, as canonical values. */
export function resultFigures(text: string): string[] {
  const out = new Set<string>();
  for (const match of text.matchAll(new RegExp(FIGURE_SOURCE, 'g'))) {
    const canonical = canonicalFigure(match[0]);
    if (canonical) out.add(canonical);
  }
  return [...out];
}
